import { logger } from '../utils/logger';
import { useCallback } from 'react';
import { useAppSelector } from './redux';
import { useMsalAuth } from './useMsalAuth';

/**
 * Custom hook for authentication operations
 *
 * Updated to use Microsoft Entra External ID (MSAL) authentication.
 * Provides a compatible interface with the previous JWT-based auth.
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout } = useAuth();
 *
 * // Login redirects to Entra External ID
 * await login();
 *
 * // Logout clears MSAL session
 * await logout();
 * ```
 */
export const useAuth = () => {
  const authState = useAppSelector((state) => state.auth);
  const { login: msalLogin, logout: msalLogout, isLoading, error } = useMsalAuth();

  /**
   * Login using Entra External ID
   *
   * Note: This redirects the user to Entra's hosted login page.
   * The old signature accepted credentials, but MSAL handles auth externally.
   *
   * @deprecated The credentials parameter is no longer used.
   */
  const handleLogin = useCallback(
    async (_credentials?: any) => {
      // Ignore credentials parameter - MSAL handles authentication
      if (_credentials) {
        logger.warn('[useAuth] Credentials parameter is deprecated with MSAL authentication');
      }
      await msalLogin();
    },
    [msalLogin]
  );

  /**
   * Register new user
   *
   * @deprecated With Entra External ID, user registration is handled by Entra.
   * Users should be redirected to the login flow which includes sign-up.
   */
  const handleRegister = useCallback(
    async (_userData?: any) => {
      logger.warn('[useAuth] Registration is handled by Entra External ID');
      // Redirect to login which includes sign-up option
      await msalLogin();
    },
    [msalLogin]
  );

  /**
   * Logout and clear session
   *
   * Delegates entirely to MSAL's logoutRedirect which navigates the browser
   * to Entra's end-session endpoint. The page reload after the round-trip
   * resets all Redux state, so there is no need (and it is actively harmful)
   * to dispatch clearEntraAccount() here — doing so sets isAuthenticated=false
   * too early, causing ProtectedRoute to render EntraRedirect while
   * logoutRedirect is still in flight, resulting in interaction_in_progress
   * errors or a stuck "Redirecting to sign in..." spinner.
   */
  const handleLogout = useCallback(async () => {
    await msalLogout();
  }, [msalLogout]);

  /**
   * Validate session
   *
   * @deprecated With MSAL, session validation is handled automatically.
   * MSAL manages token refresh and session state internally.
   */
  const handleValidateSession = useCallback(() => {
    logger.warn('[useAuth] Session validation is handled automatically by MSAL');
    return Promise.resolve();
  }, []);

  return {
    // State
    user: authState.user,
    userInfo: authState.userInfo, // Entra user info
    account: authState.account, // MSAL account
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading || isLoading,
    isInitialized: authState.isInitialized,
    rolesLoading: authState.rolesLoading, // True while fetching roles from access token
    rolesInitialized: authState.rolesInitialized, // True after roles fetch completes
    authReadyState: authState.authReadyState, // Single source of truth for auth state
    error: authState.error || (error ? error.message : null),

    // Actions
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    validateSession: handleValidateSession,
  };
};
